import { useEffect, useState } from 'react';

import { de } from '../../i18n/de';
import { candidatePhraseRepo, categoryRepo, historyRepo, phraseRepo } from '../../data/repositories';
import { categoriesWithRoom } from '../../core/authoring/capacity';
import { KEEP_HOME, shortLabelFor } from '../../core/authoring/keep';
import { mineCandidates } from '../../core/learning/mining';
import { proposeOrder } from '../../core/learning/ordering';
import { useLearning } from '../../core/learning/LearningService';
import { prerenderMissing } from '../../core/speech/prerender';
import { speechService } from '../../core/speech/SpeechService';
import { useProfileStore } from '../../core/profile/ProfileStore';
import { CareButton } from './BetreuerModus';
import type { CandidatePhrase, Category, Phrase } from '../../core/types';

/**
 * The learning panel: what the device has picked up, and what it would like to make into a button.
 *
 * `Als Satz speichern?` is the most valuable behaviour in Phase 2 — the person's own repeated sentences
 * becoming one-tap tiles. Everything else here exists to serve that: the learned-word count so a
 * caregiver can see the feature is alive during the weeks before it feels clever, and the reorder
 * proposal so the board can follow the person's life without ever rearranging itself unasked.
 */
export function LearningPanel(): React.JSX.Element {
  const wordCount = useLearning((s) => s.wordCount);
  const voiceId = useProfileStore((s) => s.settings.voiceId);

  const [candidates, setCandidates] = useState<CandidatePhrase[] | null>(null);
  const [busy, setBusy] = useState(false);
  /** The categories with a free slot, and the one an accepted candidate goes to (`AUT-08`). */
  const [homes, setHomes] = useState<Category[]>([]);
  const [homeId, setHomeId] = useState('');

  /**
   * Mining runs here rather than on every utterance: it is O(the 14-day window) and nobody is waiting
   * for it. Doing it on the speech path would put work between the person and being heard for a result
   * only a caregiver ever sees.
   */
  const mine = async (): Promise<void> => {
    setBusy(true);
    try {
      const [history, existing, phrases, categories] = await Promise.all([
        historyRepo.all(),
        candidatePhraseRepo.all(),
        phraseRepo.all(),
        categoryRepo.all(),
      ]);

      const found = mineCandidates({
        history,
        existing,
        existingPhraseTexts: phrases.map((phrase) => phrase.text),
        nowMs: Date.now(),
      });

      // Recomputed on every run, because accepting one candidate can be what fills the last slot.
      const withRoom = categoriesWithRoom(categories, phrases);
      setHomes(withRoom);
      // `KEEP_HOME` is the same preference the keep offer holds, and for the same reason: a sentence
      // that came out of talking belongs with the talking. It ships full, so it is a preference and
      // not a destination — `AUT-08` is why the panel asks rather than assumes.
      setHomeId((previous) =>
        withRoom.some((category) => category.id === previous)
          ? previous
          : (withRoom.find((category) => category.id === KEEP_HOME)?.id ?? withRoom[0]?.id ?? ''),
      );

      // Persisted so the export carries them and counts accumulate across runs.
      await candidatePhraseRepo.bulkPut(found);
      setCandidates(found.filter((candidate) => !candidate.dismissed));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void mine();
    // Once, on opening the panel.
  }, []);

  const accept = async (candidate: CandidatePhrase): Promise<void> => {
    // Nowhere with a free slot is nowhere the person could see it, so nothing is written (`AUT-08`).
    if (homeId === '') return;

    const phrase: Phrase = {
      id: `ph-mined-${Date.now()}`,
      categoryId: homeId,
      text: candidate.text,
      shortLabel: shortLabelFor(candidate.text),
      order: 999,
      pinned: false,
      useCount: 0,
    };

    await phraseRepo.upsert(phrase);
    await candidatePhraseRepo.remove(candidate.text);

    // Rendered now, while a caregiver is here and probably online. Otherwise the newest and most-wanted
    // tile would be the one that needs the network the first time it is tapped.
    const provider = speechService.chain().find((entry) => entry.id === 'azureTts');
    if (provider) void prerenderMissing({ provider, voiceId });

    await mine();
  };

  const dismiss = async (candidate: CandidatePhrase): Promise<void> => {
    // Kept as a dismissal rather than deleted: 2.4 requires that it never resurfaces, and a caregiver
    // who has said no should not be asked again.
    await candidatePhraseRepo.upsert({ ...candidate, dismissed: true });
    await mine();
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 'var(--font-body)' }}>
      <h2 style={{ fontWeight: 700 }}>Gelernt</h2>
      <p data-testid="care-learned-count">
        {wordCount} eigene Wörter gelernt.
        {wordCount === 0
          ? ' Noch nichts — das kommt mit dem Sprechen.'
          : ' Vorschläge werden dadurch mit der Zeit besser.'}
      </p>

      <h2 style={{ fontWeight: 700 }}>{de.saveAsPhrase}</h2>
      {candidates === null ? (
        <p>…</p>
      ) : candidates.length === 0 ? (
        <p data-testid="care-no-candidates">
          Noch keine wiederholten Sätze. Ein Satz erscheint hier, wenn er dreimal in zwei Wochen
          gesprochen wurde.
        </p>
      ) : homes.length === 0 ? (
        // Every category is full, so accepting would make a tile nobody could reach. Said plainly,
        // rather than by a button that quietly does nothing (`AUT-08`).
        <p data-testid="care-candidates-no-room">{de.noRoomForPhraseAnywhere}</p>
      ) : (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {de.candidateCategory}
            <select
              data-testid="candidate-category"
              value={homeId}
              onChange={(event) => setHomeId(event.target.value)}
              style={{ minHeight: 'var(--min-target)', fontSize: 'var(--font-body)' }}
            >
              {homes.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>

          <ul
            data-testid="care-candidates"
            style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {candidates.map((candidate) => (
              <li
                key={candidate.text}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 8,
                  border: '3px solid var(--key-border)',
                  borderRadius: 'var(--radius)',
                }}
              >
                <span style={{ flex: '1 1 auto' }}>
                  «{candidate.text}» — {candidate.count}×
                </span>
                <CareButton
                  id={`candidate-accept:${candidate.text}`}
                  label="Übernehmen"
                  onPress={() => void accept(candidate)}
                />
                <CareButton
                  id={`candidate-dismiss:${candidate.text}`}
                  label="Nicht"
                  onPress={() => void dismiss(candidate)}
                />
              </li>
            ))}
          </ul>
        </>
      )}
      {busy ? null : (
        <CareButton id="care:mine-again" label="Neu suchen" onPress={() => void mine()} />
      )}

      <ResortPanel />
    </section>
  );
}

/**
 * `Neu sortieren` — proposes, never applies on its own.
 *
 * §5.4 is firm: never automatically, never mid-conversation. Somebody who has learned that a tile is
 * second in the top row does not benefit from it becoming first, so the reordering is a deliberate act
 * with the new order visible beforehand — the same shape as the calibration proposal.
 */
function ResortPanel(): React.JSX.Element {
  const [proposal, setProposal] = useState<Array<{ phrase: Phrase; order: number }> | null>(null);
  const [nothingToDo, setNothingToDo] = useState(false);

  const propose = async (): Promise<void> => {
    const phrases = await phraseRepo.all();
    const { proposal: next, changed } = proposeOrder({ phrases, nowMs: Date.now() });
    setNothingToDo(!changed);
    setProposal(changed ? next : null);
  };

  const apply = async (): Promise<void> => {
    if (!proposal) return;
    for (const entry of proposal) {
      await phraseRepo.upsert({ ...entry.phrase, order: entry.order });
    }
    setProposal(null);
    setNothingToDo(true);
  };

  return (
    <>
      <h2 style={{ fontWeight: 700 }}>{de.resortTiles}</h2>
      <CareButton id="care:propose-order" label={de.resortTiles} onPress={() => void propose()} />

      {nothingToDo && proposal === null ? (
        <p data-testid="care-order-unchanged">Die Reihenfolge passt schon.</p>
      ) : null}

      {proposal ? (
        <div
          data-testid="care-order-proposal"
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <p>Neue Reihenfolge — wird erst auf Bestätigung übernommen:</p>
          <ol style={{ paddingLeft: 20 }}>
            {proposal.slice(0, 12).map((entry) => (
              <li key={entry.phrase.id}>
                {entry.phrase.shortLabel ?? entry.phrase.text}
                {entry.phrase.pinned ? ' (angepinnt)' : ''}
              </li>
            ))}
          </ol>
          <div style={{ display: 'flex', gap: 8 }}>
            <CareButton id="care-order:apply" label={de.confirm} onPress={() => void apply()} />
            <CareButton id="care-order:cancel" label={de.cancel} onPress={() => setProposal(null)} />
          </div>
        </div>
      ) : null}
    </>
  );
}

